import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Calculator, Ship, KeyRound, Loader2 } from "lucide-react";
import ConsentCheckboxes from "@/components/ConsentCheckboxes";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMPORT_IMG =
  "https://images.unsplash.com/photo-1522674149721-b0191358dc5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjYXIlMjB0cmFuc3BvcnQlMjBzaGlwfGVufDB8fHx8MTc5MzgwNjQ3M3ww&ixlib=rb-4.1.0&q=85";

const STEPS = [
  {
    icon: Search,
    title: "1. Escolha da Viatura",
    text: "Diga-nos o modelo, equipamento e orçamento. Procuramos nos mercados europeus as melhores oportunidades.",
  },
  {
    icon: Calculator,
    title: "2. Cotação Transparente",
    text: "Apresentamos o custo total — viatura, transporte, ISV e legalização — antes de qualquer compromisso.",
  },
  {
    icon: Ship,
    title: "3. Transporte & Legalização",
    text: "Tratamos do transporte, inspeção, matrícula e toda a burocracia alfandegária e fiscal.",
  },
  {
    icon: KeyRound,
    title: "4. Entrega Chave na Mão",
    text: "Recebe a sua viatura legalizada e pronta a circular, com acompanhamento em todas as fases.",
  },
];

const initialQuote = { name: "", phone: "", model: "", budget: "", message: "" };

export default function ImportService() {
  const [quote, setQuote] = useState(initialQuote);
  const [consent, setConsent] = useState({ accept_policy: false, accept_marketing: false });
  const [sending, setSending] = useState(false);
  const settings = useSiteSettings();

  const set = (k) => (e) => setQuote((q) => ({ ...q, [k]: e.target.value }));

  const submitQuote = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        name: quote.name,
        email: "noreply@solutioncarpremium.pt",
        phone: quote.phone,
        inquiry_type: "importacao",
        message: `Modelo pretendido: ${quote.model || "a definir"} | Orçamento: ${quote.budget || "a definir"} | ${quote.message}`,
      });
      try {
        await axios.post(`${API}/privacy/consent`, {
          name: quote.name, email: "noreply@solutioncarpremium.pt",
          accept_policy: consent.accept_policy, accept_marketing: consent.accept_marketing,
          context: "importacao", policy_version: settings.privacy_policy_version || "1.0",
        });
      } catch { /* consent registado em segundo plano */ }
      toast.success("Pedido de cotação de importação enviado! Entraremos em contacto em breve.");
      setQuote(initialQuote);
      setConsent({ accept_policy: false, accept_marketing: false });
    } catch {
      toast.error("Não foi possível enviar. Verifique os dados e tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="importacao" data-testid="import-section" className="py-20 sm:py-24 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.35em] uppercase text-slate-500 mb-4">
              Importação da Europa
            </p>
            <h2
              data-testid="import-heading"
              className="font-display text-3xl sm:text-4xl tracking-tight text-[#0072C6] leading-tight"
            >
              Importe a Sua Viatura com Poupança e Segurança
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Importar um automóvel da Europa pode representar uma poupança significativa — mas exige conhecimento
              dos custos, prazos e burocracia. A SOLUTION CAR PREMIUM trata de todo o processo por si, com
              total transparência do primeiro contacto até à entrega.
            </p>
            <div className="relative mt-10 hidden lg:block">
              <div className="absolute -top-4 -left-4 w-full h-full border border-[#0072C6]/20" />
              <img
                src={IMPORT_IMG}
                alt="Transporte de viaturas da Europa"
                data-testid="import-image"
                loading="lazy"
                className="relative w-full h-72 object-cover shadow-lg"
              />
            </div>
            <a
              href="/importar"
              data-testid="import-cta"
              className="mt-8 inline-flex items-center bg-[#FF6A00] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#e05e00] transition-colors"
            >
              Pedir Cotação de Importação
            </a>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5" data-testid="import-steps">
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                data-testid={`import-step-${i + 1}`}
                className="bg-white border border-slate-200 p-7 shadow-sm hover:shadow-md hover:border-[#0072C6]/30 transition-all"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-[#0072C6] text-white mb-5">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-lg text-[#0072C6]">{title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Import quote form */}
        <div
          id="pedido-importacao"
          data-testid="import-quote-section"
          className="mt-16 bg-white border border-slate-200 shadow-sm p-7 sm:p-10"
        >
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <h3 className="font-display text-2xl text-[#0072C6]" data-testid="import-quote-heading">
                Pedido de Cotação de Importação
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">
                Diga-nos a viatura que procura e o seu orçamento. Apresentamos uma cotação com o custo
                total — viatura, transporte, ISV e legalização — sem compromisso.
              </p>
            </div>
            <form onSubmit={submitQuote} data-testid="import-quote-form" className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="imp-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Nome *
                </label>
                <input
                  id="imp-name"
                  data-testid="import-name-input"
                  type="text"
                  required
                  minLength={2}
                  value={quote.name}
                  onChange={set("name")}
                  placeholder="O seu nome"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                />
              </div>
              <div>
                <label htmlFor="imp-phone" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Telefone *
                </label>
                <input
                  id="imp-phone"
                  data-testid="import-phone-input"
                  type="tel"
                  required
                  minLength={6}
                  value={quote.phone}
                  onChange={set("phone")}
                  placeholder="+351 ..."
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                />
              </div>
              <div>
                <label htmlFor="imp-model" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Modelo pretendido
                </label>
                <input
                  id="imp-model"
                  data-testid="import-model-input"
                  type="text"
                  value={quote.model}
                  onChange={set("model")}
                  placeholder="Ex.: BMW 320d Touring"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                />
              </div>
              <div>
                <label htmlFor="imp-budget" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Orçamento (€)
                </label>
                <input
                  id="imp-budget"
                  data-testid="import-budget-input"
                  type="text"
                  value={quote.budget}
                  onChange={set("budget")}
                  placeholder="Ex.: 30 000"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="imp-message" className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Detalhes adicionais
                </label>
                <textarea
                  id="imp-message"
                  data-testid="import-message-textarea"
                  rows={3}
                  value={quote.message}
                  onChange={set("message")}
                  placeholder="Equipamento, ano, combustível, prazo…"
                  className="w-full border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#0072C6] focus:ring-1 focus:ring-[#0072C6] transition-colors resize-y"
                />
              </div>
              <div className="sm:col-span-2">
                <ConsentCheckboxes consent={consent} onChange={setConsent} idPrefix="import" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  data-testid="import-submit-button"
                  disabled={sending}
                  className="inline-flex items-center justify-center gap-2 bg-[#FF6A00] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#e05e00] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {sending ? "A enviar…" : "Pedir Cotação de Importação"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
